/**
 * Formatters for the preview metrics hover card.
 * Keeps presentation components free of fiddly numeric helpers.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowLayer } from '@trafficpulse/shared';

const LAYER_LABEL: Record<FlowLayer, string> = {
  paths: 'Path',
  landers: 'Lander',
  offers: 'Offer',
};

export function layerLabel(layer: FlowLayer): string {
  return LAYER_LABEL[layer];
}

export function fmtPctFromPerMille(perMille: number): string {
  const pct = perMille / 10;
  return `${pct.toFixed(pct >= 100 ? 1 : 2)}%`;
}

/**
 * @deprecated The UI now expresses every share as a percent. Kept for any
 * legacy diagnostics that still want the raw ‰ value.
 */
export function fmtPerMille(perMille: number): string {
  return `${(perMille / 10).toFixed(1)}%`;
}

export function fmtDelta(perMille: number): string {
  const pct = perMille / 10;
  if (Math.abs(pct) < 0.05) return '0%';
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

export function fmtScore(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (isWholeNumber(value)) return value.toFixed(1);
  const abs = Math.abs(value);
  if (abs >= 1000) return value.toFixed(2);
  if (abs >= 10) return value.toFixed(2);
  return value.toFixed(3);
}

function isWholeNumber(value: number): boolean {
  return Math.abs(value - Math.round(value)) < 1e-9;
}

/**
 * The optimizer's `scoreShare` is on the 0–1000 per-mille scale (the
 * unrounded share the allocator computed). Render it as a familiar %.
 */
export function fmtShare(share: number | undefined): string {
  if (share == null || Number.isNaN(share)) return '—';
  return `${(share / 10).toFixed(2)}%`;
}

export function deltaTone(perMille: number): 'up' | 'down' | 'flat' {
  const rounded = Math.round(perMille);
  if (rounded > 0) return 'up';
  if (rounded < 0) return 'down';
  return 'flat';
}
